import { useState, useEffect } from 'react';
import { Box, Grid, Autocomplete, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';

import printPrettyPhoneNumber from '../../utils/printPrettyPhoneNumber';
import CONFIG from '@/config';
import { formDataInstance } from '@/common/axios';
import { getRecentlyViewed } from '@/common/utils/recentClientSearch';
import { Customer } from '@/views/Pages/Customer/CustomerProfile/CustomerTabs/Account/Types';
import { InputAdornment } from '@mui/material';
import { ClientSearchIcon } from '@/common/icons/ClientSearchIcon';

let i = 0;
let timer = undefined;
const searchDelay = 240;
const minCharsToSearch = 3;

const useStyles: any = makeStyles(() => ({
  input: {
    backgroundColor: 'white',
    borderRadius: 4,
    paddingTop: 0,
    paddingLeft: 4
  },
  options: {
    '& .MuiSelect-select': {
      padding: 9
    },
    '& .MuiInputBase-root': {
      height: 32,
      paddingLeft: 3
    },
    '& .MuiInputBase-input': {
      marginTop: '0 !important'
    },
    '& svg': {
      top: '4px !important'
    }
  },
  autocomplete: {
    '& label': {
      backgroundColor: 'white',
      padding: '0 6px'
    },
    '& .MuiAutocomplete-listbox': {
      paddingTop: 0
    },
    '& .MuiInputBase-root': {
      paddingRight: '10px !important'
    },
    '& .Mui-focused': {
      '& svg, & label': {
        display: 'none !important'
      },
      borderRadius: 8
    },
    '& legend': {
      display: 'none !important'
    },
    '& fieldset': {
      top: 0
    }
  },
  navSearchTextField: {
    '& .MuiInputBase-root': {
      height: '40px'
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 6,
      padding: 0,
      paddingRight: '3px !important',
      marginLeft: 8,
      '& .MuiOutlinedInput-notchedOutline': {
        border: '1px solid #dadada'
      }
    }
  },
  icon: {
    position: 'relative',
    left: 8,
    color: 'grey'
  },
  popper: {
    '& .MuiPaper-root': {
      borderRadius: '0 0 8px 8px'
    }
  },
  popperLeftNav: {
    width: '280px !important',
    paddingLeft: 8
  },
  resultCell: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between'
  },
  storeCell: {
    marginLeft: -2,
    fontWeight: 600
  },
  nameRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: 4
  },
  nameCell: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    paddingRight: 4,
    whiteSpace: 'nowrap',
    fontWeight: 600
  },
  emailCell: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    display: 'block'
  },
  recentCell: {
    padding: '8px 0 4px 10px',
    fontWeight: 600,
    width: '100%',
    display: 'flex'
  },
  apptDTCell: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: '20%',
    whiteSpace: 'nowrap'
  },
  apptDescCell: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: '60%',
    whiteSpace: 'nowrap'
  },
  apptStoreCodeCell: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: '20%',
    whiteSpace: 'nowrap'
  }
}));

const convertCustomer = c => ({
  id: c.id || c.customerId,
  name: `${c.firstName} ${c.lastName}`,
  email: c.email,
  mobilePhone: c.mobilePhone,
  store: c?.store_code ? parseInt(c?.store_code?.substr(2, 4)) : '',
  store_code: c?.homeStoreCode,
  safety_alert: c?.safetyAlert,
  appointment: c?.appointment
});

interface CustomerSearchProps {
  autoFocus?: boolean;
  onChange: (c: Customer) => void;
  handleBlur?: () => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  classes?: string;
  clearOnSelect?: boolean;
  disabled?: boolean;
  excludeIds?: number[];
  selectedCustomer?: Customer;
  showContext?: boolean;
  disableRecents?: boolean;
  navSearch?: boolean;
}

export interface CustomerDetails {
  appointment?: number;
  city?: string;
  email: string;
  firstName: string;
  lastName: string;
  hasMembership: boolean;
  homeStoreCode: string;
  id: number;
  middleName?: string;
  mobilePhone: string;
  safetyAlert: string;
  gender: string;
  state?: string;
}
interface CustomerSearchResponse {
  data: {
    data: CustomerDetails[];
  };
}

export interface CustomerDetailsResponse {
  data: {
    data: CustomerDetails;
  };
}

export const CustomerSearch = ({
  autoFocus = false,
  onChange = (c: Customer) => c,
  handleBlur = () => void 0,
  label = '',
  error = false,
  helperText = '',
  classes = undefined,
  clearOnSelect = false,
  disabled = false,
  excludeIds = [],
  selectedCustomer = null,
  showContext = true,
  disableRecents = false,
  navSearch = false
}: CustomerSearchProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<any>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<any>(selectedCustomer);
  const internalClasses = useStyles();
  const [isCustomerDetailsLoaded, setIsCustomerDetailsLoaded] =
    useState<boolean>();

  const getCustomerDetails = async (id: number) => {
    setLoading(true);

    return formDataInstance
      .get(`${CONFIG.USERS_API_URL}/staff/users?customerId=${id}`)
      .then((response: CustomerDetailsResponse) => {
        const customerData = convertCustomer(response.data.data);
        setSelectedOption(customerData);
        return customerData;
      })
      .catch(e => {
        console.log(e);
        return null;
      })
      .finally(() => {
        setLoading(false);
        setOpen(false);
        setIsCustomerDetailsLoaded(true);
      });
  };

  const searchCustomers = async vars => {
    setLoading(true);

    await formDataInstance
      .get(`${CONFIG.USERS_API_URL}/staff/user-search`, {
        params: vars
      })
      .then((response: CustomerSearchResponse) => {
        let customers = [];
        try {
          customers = response.data.data.map(c => convertCustomer(c));
        } catch (e) {
          console.log(e);
        }
        setOptions(customers);
        if (!selectedOption?.id && inputValue?.length > 0) {
          setOpen(true);
        }
      })
      .catch(e => {
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!isCustomerDetailsLoaded && selectedOption?.id) {
      getCustomerDetails(selectedOption.id).then(updatedCustomer => {
        setCustomerSelection(updatedCustomer);
      });
    }
  }, [selectedOption, isCustomerDetailsLoaded]);

  useEffect(() => {
    if (autoFocus) {
      showRecents();
    }
  }, [autoFocus]);

  const getRecentClients = () => setOptions(getRecentlyViewed());

  const showRecents = () => {
    if (
      !disableRecents &&
      inputValue === '' &&
      getRecentlyViewed().length > 0
    ) {
      setOpen(true);
    }
  };

  const removeExcludeIds = args => {
    if (!excludeIds.length) {
      return args;
    }
    const idString = excludeIds.join(',');
    return args.filter(i => {
      return !idString.match(i.id);
    });
  };

  const onInputChange = (event, newInputValue, reason) => {
    if (reason === 'reset') {
      setInputValue(newInputValue);
      setOpen(false);
    } else if (reason === 'clear') {
      setInputValue('');
      setSelectedOption(null);
      onChange(undefined);
      setOpen(false);
    } else if (reason === 'paste') {
      searchCustomers({
        query: newInputValue,
        limit: 20,
        searchAllStores: true
      });
      setOpen(true);
    } else if (reason === 'input') {
      setInputValue(newInputValue);
      if (timer) {
        clearTimeout(timer);
      }

      if (!disabled) {
        setOptions([]);
        timer = setTimeout(() => {
          if (newInputValue?.length >= minCharsToSearch) {
            setOpen(true);
            searchCustomers({
              query: newInputValue,
              limit: 20,
              searchAllStores: true
            });
          } else {
            getRecentClients();
          }
        }, searchDelay);
      }
    }
  };

  const handleAutocomplete = (event, newValue) => {
    if (newValue != null) {
      getRecentClients();
      if (newValue?.is_recent) {
        getCustomerDetails(newValue?.id).then(updatedCustomer => {
          setCustomerSelection(updatedCustomer);
        });
      } else {
        setCustomerSelection(newValue);
      }
      setOpen(false);
    } else {
      setSelectedOption(null);
    }
  };

  const setCustomerSelection = customer => {
    onChange(customer);
    if (clearOnSelect) {
      setSelectedOption(null);
      setInputValue(null);
    } else {
      setSelectedOption(customer);
      setInputValue(customer?.name);
    }
  };

  return (
    <Grid container className={classes ? classes : ''}>
      <Grid
        item
        xs={12}
        spacing={0}
        className={!classes ? internalClasses.autocomplete : ''}
      >
        <Autocomplete
          popupIcon={''}
          disabled={disabled}
          disablePortal={!navSearch}
          blurOnSelect
          className={loading ? 'loading' : ''}
          ListboxProps={{
            style: {
              maxHeight: '60vh',
              minWidth: 200,
              paddingTop: 0,
              paddingBottom: 0
            }
          }}
          open={open}
          options={loading ? [] : options}
          value={selectedOption}
          inputValue={inputValue}
          onBlur={() => {
            if (selectedOption === null) {
              setInputValue('');
            }
            handleBlur();
          }}
          onClose={() => setOpen(false)}
          onChange={handleAutocomplete}
          loading={false}
          getOptionLabel={options => options.name || ''}
          noOptionsText={null}
          onInputChange={onInputChange}
          filterOptions={removeExcludeIds}
          classes={{
            option: 'MuiAutocomplete-option customer-selector-option'
          }}
          componentsProps={{
            popper: {
              className: !navSearch
                ? internalClasses.popper
                : internalClasses.popperLeftNav
            }
          }}
          onFocus={() => {
            getRecentClients();
            showRecents();
          }}
          renderInput={params => {
            return navSearch ? (
              <TextField
                autoFocus={autoFocus}
                fullWidth
                className={internalClasses.navSearchTextField}
                onBlur={handleBlur}
                {...params}
                placeholder="Name, phone or email"
                variant="outlined"
                onPaste={event => {
                  setOpen(true);
                  onInputChange(
                    event,
                    event.clipboardData.getData('text'),
                    'paste'
                  );
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{
                        margin: theme => theme.spacing(0, 1, 0, 2)
                      }} // Adjust margin for icon
                    >
                      <ClientSearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: null
                }}
              />
            ) : (
              <TextField
                autoFocus={autoFocus}
                fullWidth
                {...params}
                label={label ? label : 'Search by name, phone or email'}
                error={error}
                helperText={helperText}
              />
            );
          }}
          renderOption={(props, result) => {
            const email = result?.email || '--';
            const phone = result?.mobilePhone
              ? printPrettyPhoneNumber(result?.mobilePhone)
              : '--';
            const apptDescription =
              result?.appointment?.purchasableItemDescription || '';
            const apptDT = result?.appointment
              ? moment(result?.appointment?.start).format('M/D/YY')
              : '';
            const apptStoreCode = result?.appointment?.storeCode || '';
            const showRecentHeader =
              inputValue?.length === 0 &&
              options?.length > 0 &&
              result == options[0];
            return (
              <>
                {showRecentHeader && (
                  <Typography
                    className={internalClasses.recentCell}
                    variant="caption2"
                    color="textSecondary"
                  >
                    Recently viewed
                  </Typography>
                )}
                <li
                  {...props}
                  style={{
                    flexDirection: 'column',
                    padding: '12px 10px'
                  }}
                  key={i++}
                >
                  <Box className={internalClasses.nameRow}>
                    <Typography
                      className={internalClasses.nameCell}
                      variant="caption2"
                      color="textPrimary"
                      title={result?.name}
                    >
                      {result?.name}
                    </Typography>
                    <Typography
                      className={internalClasses.storeCell}
                      variant="caption2"
                      color="textSecondary"
                    >
                      {result?.store_code}
                    </Typography>
                  </Box>
                  <Box className={internalClasses.resultCell}>
                    <Typography
                      variant="caption2"
                      color="textSecondary"
                      title={phone}
                    >
                      {phone}
                    </Typography>
                  </Box>
                  <Box className={internalClasses.resultCell}>
                    <Typography
                      className={internalClasses.emailCell}
                      variant="caption2"
                      color="textSecondary"
                      title={email}
                    >
                      {email}
                    </Typography>
                  </Box>
                  {showContext && (
                    <Box className={internalClasses.resultCell}>
                      <Typography
                        className={internalClasses.apptDTCell}
                        variant="caption2"
                        color="textSecondary"
                        title={apptDT}
                      >
                        {apptDT}
                      </Typography>
                      <Typography
                        className={internalClasses.apptDescCell}
                        variant="caption2"
                        color="textSecondary"
                        title={apptDescription}
                      >
                        {apptDescription}
                      </Typography>
                      <Typography
                        className={internalClasses.apptStoreCodeCell}
                        variant="caption2"
                        color="textSecondary"
                        title={apptStoreCode}
                      >
                        {apptStoreCode}
                      </Typography>
                    </Box>
                  )}
                </li>
              </>
            );
          }}
        />
      </Grid>
    </Grid>
  );
};
